import type { VideoEvidence } from "../ChatEventTypes";
import type { SpeakingBlockState } from "../chat-panel/chatPanelState.ts";
import type { MentionedVideoItem } from "../components/VideoMention.tsx";
import type { TextOrMentionSegment } from "./parseMentionJsonInText";

/** The range fields alone, so that a clip with no citation marker also fits. */
type MentionClip = Pick<MentionedVideoItem, "start_time_sec" | "end_time_sec">;

export function mentionClipRange(item: MentionClip): {
	start: number;
	end: number;
} {
	const start = item.start_time_sec;
	const end = item.end_time_sec;

	if (start != null && end != null && end > start) {
		return { start, end };
	}

	const resolvedStart = start ?? 0;
	return { start: resolvedStart, end: resolvedStart + 10 };
}

export function mentionClipDurationSec(item: MentionClip): number {
	const { start, end } = mentionClipRange(item);
	return end - start;
}

/**
 * Whether the mention stands for a range within its video rather than for the
 * whole of it. A whole-video mention — one inserted from the QuestionBox's
 * context selector, say — carries no bounds at all, which is what separates it
 * from a clip whose range happens to start at the top of the video.
 */
export function isMentionClip(item: MentionClip): boolean {
	return item.start_time_sec != null && item.end_time_sec != null;
}

export function videoMentionFromSegment(
	seg: Extract<TextOrMentionSegment, { type: "video_mention" }>,
): MentionedVideoItem {
	return {
		id: String(seg.value.id),
		index: seg.index,
		start_time_sec: seg.value.start_time_sec ?? null,
		end_time_sec: seg.value.end_time_sec ?? null,
		thumbnail_url: seg.value.thumbnail_url,
		aspect_ratio: seg.value.aspect_ratio,
		title: seg.value.title,
		video_id: seg.value.video_id,
		video_url: seg.value.video_url,
		video_duration: seg.value.video_duration,
		hls_ready: seg.value.hls_ready,
		influencer: seg.value.influencer,
		social_account: seg.value.social_account,
		view_count: seg.value.view_count,
		post_url: seg.value.post_url,
	};
}

export function videoMentionsInBlockOrder(
	block: SpeakingBlockState,
): MentionedVideoItem[] {
	const mentions = Object.entries(block.mentions)
		.filter(
			(entry): entry is [string, { kind: "video"; data: MentionedVideoItem }] =>
				entry[1].kind === "video",
		)
		.sort(
			([placeholderA], [placeholderB]) =>
				block.markdown.indexOf(placeholderA) -
				block.markdown.indexOf(placeholderB),
		)
		.map(([, entry]) => entry.data);

	const seenIds = new Set<string>();
	return mentions.filter((mention) => {
		if (seenIds.has(mention.id)) {
			return false;
		}
		seenIds.add(mention.id);
		return true;
	});
}

export function mentionedVideoItemToVideoEvidence(
	item: MentionedVideoItem,
): VideoEvidence {
	const { start, end } = mentionClipRange(item);
	const clipDuration = end - start;

	return {
		video_url: item.video_url ?? "",
		thumbnail_url: item.thumbnail_url,
		aspect_ratio: item.aspect_ratio,
		start_time: start,
		end_time: end,
		video_duration: item.video_duration ?? 0,
		clip_duration: clipDuration,
		source_start_time: start,
		description: item.title ?? `Video ${item.index}`,
		title: item.title,
		video_id: item.video_id ?? item.id,
		// Not item.id: that's the mention's own id (VideoMentionStreamEventProcessor
		// mints a vm_ id), not a video the HLS endpoint knows.
		source_video_id: item.video_id,
		hls_ready: item.hls_ready,
		influencer: item.influencer ?? {},
		social_account: item.social_account ?? undefined,
		post_url: item.post_url ?? "",
		view_count: item.view_count,
	};
}

export type PlayVideoMentionsHandler = (
	videoMentions: MentionedVideoItem[],
	activeMentionId: string,
) => void;

export function playVideoMentions(
	videoMentions: MentionedVideoItem[],
	activeMentionId: string,
	options?: {
		appEmbedded?: boolean;
		interactionId?: string;
		onPlayVideoMentions?: PlayVideoMentionsHandler;
	},
) {
	if (options?.onPlayVideoMentions) {
		options.onPlayVideoMentions(videoMentions, activeMentionId);
		return;
	}

	openVideoMentionsPanel(videoMentions, activeMentionId, {
		appEmbedded: options?.appEmbedded,
		interactionId: options?.interactionId,
	});
}

export function openVideoMentionsPanel(
	videoMentions: MentionedVideoItem[],
	activeMentionId: string,
	options?: {
		appEmbedded?: boolean;
		interactionId?: string;
	},
) {
	const playableMentions = videoMentions.filter((mention) =>
		Boolean(mention.video_url),
	);
	if (playableMentions.length === 0) {
		return;
	}

	const videoEvidence = playableMentions.map(mentionedVideoItemToVideoEvidence);
	const activeIndex = playableMentions.findIndex(
		(mention) => mention.id === activeMentionId,
	);
	if (activeIndex < 0) {
		return;
	}

	if (options?.appEmbedded) {
		document.dispatchEvent(
			new CustomEvent("videocarouselplay", {
				detail: {
					interaction_id: options.interactionId,
					videos: videoEvidence,
					video_index: activeIndex,
				},
			}),
		);
		return;
	}

	document.dispatchEvent(
		new CustomEvent("chatevent", {
			detail: {
				type: "video_evidence",
				content: {
					is_open: true,
					video_index: activeIndex,
					video_evidence: videoEvidence,
				},
			},
		}),
	);
}
